import { clsx } from 'clsx'
import {
  Briefcase, Building2, ClipboardList, Cloud, CreditCard, Database, Download, History,
  LayoutDashboard, LineChart, LogOut, Receipt, Settings, Sparkles, Target, TrendingUp,
  Upload, Users, Users2, X, Wallet,
} from 'lucide-react'
import { NavLink } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useMember } from '../../contexts/MemberContext'
import { useTenantStore } from '../../stores/tenantStore'
import { KoremarginLogo } from '../ui/KoremarginLogo'

interface NavItem {
  to: string
  label: string
  icon: React.ElementType
  end?: boolean
  section?: string
}

interface NavGroup {
  label: string
  items: NavItem[]
}

const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Dashboard',
    items: [
      { to: '/',        label: 'Executive',      icon: LayoutDashboard, end: true, section: 'executive' },
      { to: '/revenue', label: 'Revenue Ops',    icon: TrendingUp, section: 'revenue_ops' },
      { to: '/margin',  label: 'Margin Control', icon: Target, section: 'margin' },
      { to: '/billing', label: 'Billing & AR',   icon: Receipt, section: 'billing' },
      { to: '/recurring-revenue', label: 'Recurring Revenue', icon: LineChart, section: 'recurring' },
      { to: '/business-review',   label: 'Business Review',  icon: ClipboardList },
    ],
  },
  {
    label: 'Dati',
    items: [
      { to: '/upload',            label: 'Carica file',    icon: Upload },
      { to: '/file-caricati',     label: 'File caricati',  icon: History },
      { to: '/cartelle-cloud',    label: 'Cartelle cloud', icon: Cloud },
      { to: '/sources',           label: 'Sorgenti',       icon: Database },
      { to: '/orders',            label: 'Commesse',       icon: Briefcase },
      { to: '/clienti',           label: 'Clienti',        icon: Users },
      { to: '/costi',             label: 'Costi & OPEX',   icon: Wallet, section: 'cost_entries' },
    ],
  },
]

const BOTTOM_ITEMS: NavItem[] = [
  { to: '/ai',                  label: 'AI Chat',       icon: Sparkles },
  { to: '/export',              label: 'Esporta',       icon: Download },
  { to: '/impostazioni/business-units', label: 'Business Unit', icon: Building2 },
  { to: '/impostazioni/collaboratori',  label: 'Collaboratori',  icon: Users2 },
  { to: '/impostazioni/piano',  label: 'Il mio piano',  icon: CreditCard },
  { to: '/impostazioni',        label: 'Impostazioni',  icon: Settings },
]

function SidebarNavItem({ item }: { item: NavItem }) {
  const Icon = item.icon
  return (
    <NavLink
      to={item.to}
      end={item.end}
      className={({ isActive }) =>
        clsx(
          'relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-all duration-150',
          isActive
            ? [
                'bg-gradient-to-r from-indigo-500/15 to-purple-500/5',
                'text-white font-semibold',
                'border border-indigo-500/20',
                'shadow-[0_0_12px_rgba(99,102,241,0.08)]',
              ]
            : [
                'text-[--km-text-secondary] border border-transparent',
                'hover:bg-white/[0.04] hover:text-[--km-text-primary]',
              ],
        )
      }
    >
      {({ isActive }) => (
        <>
          <Icon
            size={15}
            className={clsx(
              'shrink-0',
              isActive ? 'text-indigo-400' : '',
            )}
          />
          <span>{item.label}</span>
          {isActive && (
            <span className="ml-auto h-1.5 w-1.5 rounded-full bg-indigo-400 shadow-[0_0_6px_rgba(99,102,241,0.9)]" />
          )}
        </>
      )}
    </NavLink>
  )
}

export interface SidebarProps {
  open: boolean
  onClose: () => void
}

export function Sidebar({ open, onClose }: SidebarProps) {
  const { signOut } = useAuth()
  const { canAccess } = useMember()
  const tenantName = useTenantStore(s => s.tenantName)
  const plan = useTenantStore(s => s.plan)

  const initial = tenantName ? tenantName[0].toUpperCase() : 'K'

  return (
    <>
      {/* Mobile backdrop */}
      {open && (
        <div
          className="fixed inset-0 z-30 bg-black/70 backdrop-blur-md lg:hidden"
          onClick={onClose}
          aria-hidden="true"
          data-testid="sidebar-backdrop"
        />
      )}

      {/* Panel */}
      <aside
        className={clsx(
          'fixed inset-y-0 left-0 z-40 flex w-64 flex-col',
          'bg-[var(--km-sidebar-bg)] backdrop-blur-2xl',
          'border-r border-[var(--km-sidebar-border)]',
          'shadow-[var(--km-shadow-lg)]',
          'transition-transform duration-250',
          open ? 'translate-x-0' : '-translate-x-full',
          'lg:static lg:inset-auto lg:z-auto lg:translate-x-0',
        )}
        aria-label="Navigazione principale"
      >
        {/* Logo */}
        <div className="sidebar-header flex h-14 items-center justify-between px-4 border-b border-[var(--km-sidebar-border)] shrink-0">
          <KoremarginLogo variant="compact" />
          <button
            onClick={onClose}
            className="lg:hidden text-[--km-text-muted] hover:text-[--km-text-primary] p-1 rounded-lg transition-colors"
            aria-label="Chiudi menu"
          >
            <X size={15} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-2.5 py-4 space-y-5">
          {NAV_GROUPS.map(group => (
            <div key={group.label}>
              <p className="px-3 mb-2 text-[9px] font-bold uppercase tracking-[0.18em] text-[--km-text-disabled]">
                {group.label}
              </p>
              <div className="space-y-0.5">
                {group.items
                  .filter(item => !item.section || canAccess(item.section))
                  .map(item => (
                    <SidebarNavItem key={item.to} item={item} />
                  ))}
              </div>
            </div>
          ))}

          <div className="border-t border-[var(--km-sidebar-border)] pt-4 space-y-0.5">
            {BOTTOM_ITEMS.map(item => (
              <SidebarNavItem key={item.to} item={item} />
            ))}
          </div>
        </nav>

        {/* User footer */}
        <div className="shrink-0 border-t border-[var(--km-sidebar-border)] p-3">
          <div className="flex items-center gap-3 px-2 py-2 rounded-xl hover:bg-white/[0.04] transition-colors">
            <div className="h-8 w-8 rounded-xl gradient-brand flex items-center justify-center shrink-0 shadow-[0_0_10px_rgba(99,102,241,0.3)]">
              <span className="text-xs font-bold text-white">{initial}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-[--km-text-primary] truncate leading-tight">
                {tenantName ?? 'Azienda'}
              </p>
              <p className="text-[10px] text-[--km-text-muted] capitalize font-medium mt-0.5">
                {plan ?? 'trial'}
              </p>
            </div>
            <button
              onClick={signOut}
              className="shrink-0 text-[--km-text-muted] hover:text-red-400 transition-colors p-1.5 rounded-lg hover:bg-red-500/10"
              aria-label="Esci dall'account"
              title="Esci"
            >
              <LogOut size={13} />
            </button>
          </div>
        </div>
      </aside>
    </>
  )
}
